import { Box, TableCell } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { INK } from "../../../theme/tokens";
import type { SimulationEntry } from "../hooks/useHiringData";
import { ANIMATION_STAGGER_MS, SimulationNameCell } from "./SimulationNameCell";

// Functional / Categorical: hiring grid cell colors that must stay stable across themes.
const HIRING_GRID_COLORS = {
	WEEK_DIVIDER: "var(--inhouse-primary)",
	CELL_BORDER: "var(--inhouse-primary-alpha-08)",
	EMPTY_TEXT: "var(--inhouse-primary-light)",
} as const;

interface SimulationCellProps {
	entries: SimulationEntry[];
	date: DateTime;
	columnIndex: number;
	borderBottom?: string;
	freshKeys: Set<string>;
	roleAnimCounter?: { count: number };
	ignoreSimulation?: boolean;
}

const WEEK_DIVIDER_INDEX = 7;

export const SimulationCell: FC<SimulationCellProps> = ({ entries, date, columnIndex, borderBottom, freshKeys, roleAnimCounter, ignoreSimulation }) => {
	const hasEntries = entries.length > 0;

	const seen = new Set<string>();
	const unique = entries.filter((entry) => {
		if (seen.has(entry.nurseName)) return false;
		seen.add(entry.nurseName);
		return true;
	});

	return (
		<TableCell
			align="center"
			sx={{
				padding: "8px 12px",
				borderLeft: columnIndex === WEEK_DIVIDER_INDEX ? `2px solid ${HIRING_GRID_COLORS.WEEK_DIVIDER}` : "none",
				borderBottom: borderBottom ?? undefined,
				borderRight: `1px solid ${HIRING_GRID_COLORS.CELL_BORDER}`,
				color: hasEntries ? INK.SECONDARY : HIRING_GRID_COLORS.EMPTY_TEXT,
				p: hasEntries ? "4px 6px" : undefined,
				verticalAlign: "top",
			}}
		>
			{hasEntries ? (
				<InHouseTooltip
					title={
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
							}}
						>
							{unique.map((entry) => (
								<Box key={entry.nurseName}>
									{entry.nurseName} ({entry.shiftType})
								</Box>
							))}
						</Box>
					}
					placement="top"
					arrow
				>
					<div>
						{unique.map((entry) => {
							const key = `${entry.role}-${date.toISODate()}-${entry.nurseName}-${entry.shiftType}`;
							const shouldAnimate = !ignoreSimulation && freshKeys.has(key);
							const delay = shouldAnimate && roleAnimCounter ? roleAnimCounter.count++ * ANIMATION_STAGGER_MS : 0;
							return <SimulationNameCell key={key} name={entry.nurseName} isNewHire={entry.isNewHire} delay={delay} animate={shouldAnimate} />;
						})}
					</div>
				</InHouseTooltip>
			) : (
				"-"
			)}
		</TableCell>
	);
};
